import { useGetTokenSSOCannyQuery } from '@/redux/api/api.caller';
import { Spinner } from '@shopify/polaris';
import { useEffect, useCallback, memo } from 'react';
import { FeedbackWidgetProps } from './config';
import { CANNY_SDK_ID, CANNY_SDK_URL } from '../../config';

declare global {
  interface Window {
    Canny: any;
  }
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ basePath = '/feedback', theme = 'light', onLoad }) => {
  const { data: ssoTokenData, isError, isLoading, error } = useGetTokenSSOCannyQuery();

  const ssoToken = ssoTokenData?.data?.ssoToken;

  const renderWidget = useCallback(() => {
    if (!window.Canny || !ssoToken) return;

    try {
      window.Canny('render', {
        boardToken: process.env.REACT_APP_CANNY_BOARD_TOKEN,
        basePath,
        ssoToken,
        theme,
      });

      onLoad?.();
    } catch (error) {
      console.error('Failed to render Feedback widget:', error);
    }
  }, [basePath, ssoToken, theme, onLoad]);

  const loadCannySDK = useCallback(() => {
    // Return early if SDK is already loaded or being loaded
    if (document.getElementById(CANNY_SDK_ID)) {
      // If SDK is loaded and we have token, render widget
      if (window.Canny && ssoToken) {
        renderWidget();
      }
      return;
    }

    const script = document.createElement('script');
    script.id = CANNY_SDK_ID;
    script.src = CANNY_SDK_URL;
    script.async = true;
    script.onload = () => {
      if (ssoToken) {
        renderWidget();
      }
    };
    script.onerror = () => {
      console.error('Failed to load Canny SDK');
    };

    document.head.appendChild(script);
  }, [ssoToken, renderWidget]);

  // Load SDK when component mounts
  useEffect(() => {
    loadCannySDK();
  }, [loadCannySDK]);

  // Render widget when both SDK and token are available
  useEffect(() => {
    if (window.Canny && ssoToken) {
      renderWidget();
    }
  }, [ssoToken, renderWidget]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner size="large" />
      </div>
    );
  }

  if (isError) {
    const errorMessage = (error as any)?.data?.message || 'Failed to load feedback widget';
    return <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{errorMessage}</div>;
  }

  return <div data-canny />;
};

export default memo(FeedbackWidget);
